import { useState } from 'react'

import { api } from '../api'
import { initialAttribution } from '../attribution'
import { currentLang, t } from '../i18n'
import LangSwitch from '../components/LangSwitch'
import { askWriteAccess, haptic, notify } from '../telegram'
import type { LevelSource, PlayerMe } from '../types'

interface Props {
  city: string
  onDone: (me: PlayerMe) => void
}

const MANUAL_STEPS = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5]

/** "2,84" и "2.84" — одно и то же; всё остальное — не число */
function parseLevel(raw: string): number | null {
  const value = Number(raw.replace(',', '.').trim())
  return raw.trim() !== '' && Number.isFinite(value) && value >= 0 && value <= 7 ? value : null
}

/** Стартовый уровень. На первом плане — то, что человек знает сам: рейтинг
 *  Playtomic или собственная оценка. Опрос — запасной путь: он грубее и,
 *  по отзывам первых игроков, занижал уровень. */
export default function Onboarding({ city, onDone }: Props) {
  const [source, setSource] = useState<LevelSource>('playtomic')
  const [playtomic, setPlaytomic] = useState('')
  const [manual, setManual] = useState(3)
  const [months, setMonths] = useState(6)
  const [frequency, setFrequency] = useState(4)
  const [background, setBackground] = useState(false)
  const [side, setSide] = useState<'left' | 'right' | 'both'>('both')
  const [saving, setSaving] = useState(false)
  // Все новые начинают на испанском — здесь же можно сразу переключиться
  const [, rerender] = useState<string>(currentLang())

  const level = source === 'playtomic' ? parseLevel(playtomic) : source === 'manual' ? manual : null

  async function submit() {
    if (source !== 'survey' && level === null) return notify(t().onbLevelError)
    setSaving(true)
    haptic('medium')
    try {
      const me = await api.onboarding({
        source,
        level,
        months_playing: months,
        games_per_month: frequency,
        racket_sports_background: background,
        side,
        // Язык интерфейса, который человек уже видит (по языку Telegram). Раньше
        // здесь стояло 'ru' — испаноязычный игрок после опроса получал русский
        language: currentLang(),
        city,
        ...initialAttribution(),
      })
      await askWriteAccess()
      onDone(me)
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Не удалось сохранить')
    } finally {
      setSaving(false)
    }
  }

  const option = (value: LevelSource, title: string, hint: string, body: React.ReactNode) => (
    <div className={`option${source === value ? ' on' : ''}`}>
      <button
        className="option-head"
        role="radio"
        aria-checked={source === value}
        onClick={() => {
          haptic()
          setSource(value)
        }}
      >
        <span className="option-mark" aria-hidden />
        <span style={{ minWidth: 0 }}>
          <span className="option-title">{title}</span>
          <span className="muted small" style={{ display: 'block' }}>
            {hint}
          </span>
        </span>
      </button>
      {source === value && <div className="option-body">{body}</div>}
    </div>
  )

  return (
    <div>
      <LangSwitch onChange={rerender} />
      <h1 className="big">{t().onbLevelTitle}</h1>
      <p className="muted" style={{ margin: '4px 0 14px' }}>
        {t().onbLevelSub}
      </p>

      <div role="radiogroup" aria-label={t().onbLevelTitle}>
        {option(
          'playtomic',
          t().onbPlaytomic,
          t().onbPlaytomicHint,
          <>
            <input
              type="text"
              inputMode="decimal"
              className="level-input"
              placeholder={t().onbPlaytomicPlaceholder}
              aria-label={t().onbPlaytomic}
              value={playtomic}
              maxLength={4}
              onChange={(e) => setPlaytomic(e.target.value.replace(/[^\d.,]/g, ''))}
            />
            {level !== null && (
              <div className="muted small" style={{ marginTop: 6 }}>
                {t().levelBand(level)}
              </div>
            )}
          </>,
        )}

        {option(
          'manual',
          t().onbManual,
          t().onbManualHint,
          <>
            <div className="chips">
              {MANUAL_STEPS.map((value) => (
                <button
                  key={value}
                  className="chip"
                  aria-pressed={manual === value}
                  onClick={() => setManual(value)}
                >
                  {value.toFixed(1)}
                </button>
              ))}
            </div>
            <div className="level-band">
              <b>{manual.toFixed(1)}</b> — {t().levelBand(manual)}
            </div>
          </>,
        )}

        {option(
          'survey',
          t().onbSurvey,
          t().onbSurveyHint,
          <>
            <label style={{ marginTop: 0 }}>{t().onbMonths}</label>
            <input
              type="number"
              min={0}
              max={600}
              value={months}
              onChange={(e) => setMonths(Number(e.target.value))}
            />
            <label>{t().onbFrequency}</label>
            <input
              type="number"
              min={0}
              max={60}
              value={frequency}
              onChange={(e) => setFrequency(Number(e.target.value))}
            />
            <label>
              <span className="row" style={{ gap: 10 }}>
                <input
                  type="checkbox"
                  style={{ width: 20 }}
                  checked={background}
                  onChange={(e) => setBackground(e.target.checked)}
                />
                {t().onbBackground}
              </span>
            </label>
          </>,
        )}
      </div>

      <label>{t().onbSide}</label>
      <div className="chips">
        {(['left', 'right', 'both'] as const).map((value) => (
          <button key={value} className="chip" aria-pressed={side === value} onClick={() => setSide(value)}>
            {t()[value]}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 24 }}>
        <button style={{ width: '100%' }} disabled={saving} onClick={submit}>
          {saving ? t().loading : t().save}
        </button>
      </div>
    </div>
  )
}
